import { useState, useEffect, useCallback } from 'react'

const STORAGE_KEY = 'circleup-theme-mode'

export type ThemeMode = 'auto' | 'light' | 'dark'

function isNightTime(): boolean {
  const hour = new Date().getHours()
  return hour >= 19 || hour < 7
}

function resolveIsDark(mode: ThemeMode): boolean {
  if (mode === 'dark') return true
  if (mode === 'light') return false
  return isNightTime()
}

export function useDarkMode() {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'auto' || stored === 'light' || stored === 'dark') return stored
    // Migrate from old boolean storage
    const oldKey = 'circleup-dark-mode'
    const old = localStorage.getItem(oldKey)
    if (old !== null) {
      localStorage.removeItem(oldKey)
      const migrated: ThemeMode = old === 'true' ? 'dark' : 'light'
      localStorage.setItem(STORAGE_KEY, migrated)
      return migrated
    }
    return 'auto'
  })

  const isDark = resolveIsDark(mode)

  // Apply the dark class
  useEffect(() => {
    const root = document.documentElement
    if (isDark) {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
  }, [isDark])

  // Persist mode
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, mode)
  }, [mode])

  // Re-check every minute when in auto mode
  const [, setTick] = useState(0)
  useEffect(() => {
    if (mode !== 'auto') return
    const interval = setInterval(() => setTick((t) => t + 1), 60_000)
    return () => clearInterval(interval)
  }, [mode])

  // Cycle: auto → light → dark → auto
  const toggle = useCallback(() => {
    setMode((prev) => {
      if (prev === 'auto') return 'light'
      if (prev === 'light') return 'dark'
      return 'auto'
    })
  }, [])

  return { isDark, mode, setMode, toggle }
}
